import { AvailableServices } from '@vendasta/integrations';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, shareReplay, tap, startWith } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MeetingSchedulerServiceModule } from '../data-providers/meeting-scheduler-service.module';
import { LOCAL_STORAGE_TOKEN } from '../data-providers/local-storage';
import { MeetingSchedulerStoreService } from '../data-providers/meeting-scheduler-store.service';

export enum View {
  WIZARD = 'WIZARD',
  SETTINGS = 'SETTINGS',
  LOADING = 'LOADING',
}

interface State {
  view: View;
  // 0 - calendar, 1 - meeting app, 2 - availability
  wizardStep: number;
  meetingAppSelection: AvailableServices | undefined;
}

const defaultState: State = Object.freeze({
  view: View.SETTINGS,
  wizardStep: 0,
  meetingAppSelection: undefined,
});

const SCHEDULE_SETTINGS_VIEW_KEY = 'bookmenow-schedule-settings-key';

const wizardState: State = Object.freeze({
  view: View.WIZARD,
  wizardStep: 0,
  meetingAppSelection: AvailableServices.GOOGLE_MEET,
});

@Injectable({
  providedIn: MeetingSchedulerServiceModule,
})
export class ScheduleSettingsViewService {
  private readonly state$$: BehaviorSubject<State> = new BehaviorSubject(this.loadInitialState());
  private readonly state$: Observable<State>;

  readonly view$: Observable<View>;
  readonly wizardStep$: Observable<number>;
  readonly meetingAppSelection$: Observable<AvailableServices | undefined>;

  constructor(
    @Inject(LOCAL_STORAGE_TOKEN) private readonly localStorage: Storage,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
  ) {
    this.state$ = this.state$$.pipe(
      tap((state) => this.storeState(state)),
      shareReplay(1),
    );
    this.view$ = this.state$.pipe(map((s) => s.view));

    // Make wizardStep$ reactive to both query parameters and store service configuration
    this.wizardStep$ = combineLatest([
      this.route.queryParams.pipe(startWith(this.route.snapshot.queryParams)),
      this.meetingSchedulerStoreService.loadHasHostEverBeenConfigured(),
    ]).pipe(
      map(([params, isHostConfigured]) => {
        const step = Number(params['step']);
        if (!isNaN(step)) {
          return step;
        }

        // If host is not configured, use the wizard step from store service (based on onBoardingState)
        if (!isHostConfigured) {
          const storeWizardStep = this.meetingSchedulerStoreService.wizardStep;
          if (storeWizardStep !== undefined) {
            return storeWizardStep;
          }
        }

        return this.state$$.getValue().wizardStep;
      }),
      shareReplay(1),
    );

    this.meetingAppSelection$ = this.state$.pipe(map((s) => s.meetingAppSelection));
  }

  setView(view: View): void {
    this.state$$.next({ ...this.state$$.getValue(), view });
  }

  setWizardStep(wizardStep: number): void {
    this.state$$.next({ ...this.state$$.getValue(), wizardStep });
    // Update route query parameters to trigger stepper navigation
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step: wizardStep },
      queryParamsHandling: 'merge',
    });
  }

  setMeetingAppSelection(meetingAppSelection: AvailableServices): void {
    this.state$$.next({ ...this.state$$.getValue(), meetingAppSelection });
  }

  resetStateStorage(): void {
    this.localStorage.removeItem(SCHEDULE_SETTINGS_VIEW_KEY);
  }

  private storeState(state: State): void {
    this.localStorage.setItem(SCHEDULE_SETTINGS_VIEW_KEY, JSON.stringify(state));
  }

  private loadInitialState(): State {
    const wizardView = this.router.url.includes('/settings');
    const { calendarConnected, conferencingConnected } = this.route.snapshot.queryParams;
    if (wizardView) {
      // Check for query parameters first (for backward compatibility)
      if (calendarConnected != undefined) {
        return Object.freeze({
          view: View.WIZARD,
          wizardStep: 1,
          meetingAppSelection: AvailableServices.GOOGLE_MEET,
        });
      }
      if (conferencingConnected != undefined) {
        return Object.freeze({
          view: View.WIZARD,
          wizardStep: 2,
          meetingAppSelection: undefined,
        });
      }

      return wizardState;
    }
    return defaultState;
  }
}
